import {
  Component, Input, HostBinding, ChangeDetectionStrategy, Output, EventEmitter, ViewChild,
  ChangeDetectorRef, OnInit,
} from '@angular/core';
import { Location } from '@angular/common';
import {
  trigger, state, style, transition, animate,
} from '@angular/animations';
import { BehaviorSubject } from 'rxjs';
import * as _ from 'lodash';

import { Segment } from 'shared/segments-navigation/segment.interface';

import { SegmentsNavigationV2Component } from 'shared/segments-navigation/segments-navigation-v2/segments-navigation-v2.component';
import { DynamicNavsComponent } from 'shared/nav/dynamic-navs/dynamic-navs.component';
import { LoaderComponent } from 'shared/loader/loader.component';


@Component({
  moduleId: module.id,
  selector: 'details-page-modal',
  templateUrl: 'details-page-modal.component.html',
  styleUrls: ['details-page-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('showAnimation', [
      transition('void => *', [
        style({
          opacity: 0,
          transform: 'translate3d(0, 25px, 0) scale(0.98)'
        }),
        animate('0.15s ease-in-out')
      ]),
    ]),
    trigger('hideAnimation', [
      transition('* => true', animate('0.25s ease-in-out')),
      state('true', style({transform: 'translate3d(0, 25px, 0)', opacity: 0})),
    ])
  ],
})
export class DetailsPageModalComponent implements OnInit {

  @HostBinding('@showAnimation')
  @Input() showPageAnimation: boolean = true;

  @HostBinding('@hideAnimation')
  @Input() hidePageAnimation: boolean = false;

  @Input() navigationSegments: Array<Segment> = [];

  @Input() rightPanelNavs: Array<any> = [];

  @Input() urlWithoutSegment: string; // is used for updating location url after changing segment

  @Input() hideViewEditBtn: boolean = false;

  @Input() showSaveBtn: boolean = false;

  @Output() selectedSegment: EventEmitter<Segment> = new EventEmitter<Segment>();
  @Output() editModeChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() cancelChanges: EventEmitter<any> = new EventEmitter<any>();
  @Output() saveChanges: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild(SegmentsNavigationV2Component, {static: false})
  segmentsNavigation: SegmentsNavigationV2Component | void;

  @ViewChild(DynamicNavsComponent, {static: false})
  dynamicNavs: DynamicNavsComponent | void;

  @ViewChild(LoaderComponent, {static: false})
  loader: LoaderComponent | void;

  rightPanelMovedOut: boolean = false; // indicate right panel toggle
  private editModeSubj: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /******************************************/
  /* Getter Functions                       */
  /******************************************/

  get activeRightNavName(): any {
    return _.get(this.dynamicNavs, 'activeNav.name');
  }

  get activeRightNav(): any {
    return _.get(this.dynamicNavs, 'componentRef.instance');
  }

  get isEditMode(): boolean {
    return this.editModeSubj.getValue();
  }

  set isEditMode(value: boolean) {
    this.editModeSubj.next(value);
  }

  constructor(
    private ref: ChangeDetectorRef,
    private location: Location,
  ) {}

  ngOnInit() {
    this.updateSavedRightPanelStatus();
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onViewEditSaveClick() {
    if (this.showSaveBtn) {
      this.saveChanges.emit();
    } else {
      this.toggleEditMode();
    }
  }

  onCancelClick() {
    this.cancelChanges.emit();
  }

  onSegmentSelect(segment: Segment) {
    this.selectedSegment.emit(segment);
    this.updateLocationUrl(segment.name);
  }

  onMovedOutChange(rightPanelMovedOut: boolean) {
    this.saveRightPanelStatus(rightPanelMovedOut);
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  public setActiveNav(segment: string): void {
    if (this.segmentsNavigation instanceof SegmentsNavigationV2Component) {
      this.segmentsNavigation.setActive(segment);
    }
    this.ref.markForCheck();
  }

  public toggleNavCircles(func: Function): void {
    if (this.segmentsNavigation instanceof SegmentsNavigationV2Component) {
      this.segmentsNavigation.toggleCircles(func);
    }
    this.ref.markForCheck();
  }

  public setEditMode(editMode: boolean): void {
    this.isEditMode = editMode;
    this.editModeChanged.emit(this.isEditMode);
  }

  public toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
    this.editModeChanged.emit(this.isEditMode);
  }

  public showLoader() {
    if (this.loader instanceof LoaderComponent) {
      this.loader.show();
    }
    this.ref.markForCheck();
  }

  public hideLoader() {
    if (this.loader instanceof LoaderComponent) {
      this.loader.hide();
    }
    this.ref.markForCheck();
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  /*
    * Rwerite browser url after segment has changed
  */
  private updateLocationUrl(segmentName: string): void {
    this.location.go(`${this.urlWithoutSegment}/${segmentName}`);
  }

  private updateSavedRightPanelStatus() {
    const savedData = localStorage.getItem(`productDetails.rightPanelStatus`);
    if (savedData && savedData === 'true') {
      this.rightPanelMovedOut = true;
    }
  }

  private saveRightPanelStatus(status: boolean) {
    localStorage.setItem(`productDetails.rightPanelStatus`, `${status}`);
  }
}
